import { useMemo, useState } from "react";
import { Box } from "@mantine/core";

import { useGetAttendance } from "../../../api/insights";
import { DailyAreaChart, RankedBarChart, formatDay } from "../charts";
import SectionShell, {
  Stat,
  StatGrid,
  formatNum,
  formatPct,
} from "../SectionShell";
import { useChartTimeFrame } from "../useChartTimeFrame";
import { sliceByDay } from "../timeFrame";

const screenDay = (r) => String(r.hour).slice(0, 10);

const PAGE_PALETTE = [
  "blue.6",
  "teal.6",
  "grape.6",
  "orange.6",
  "lime.6",
  "pink.6",
  "cyan.6",
  "yellow.7",
  "indigo.6",
  "red.6",
  "green.6",
  "violet.6",
  "gray.6",
];

const PAGES_CHART_LIMIT = 10;

function AttendeeAccess({ eventId }) {
  const { data, isPending, isError, error } = useGetAttendance(eventId);
  const tickets = data?.tickets;
  const screens = data?.screenViews;

  const [pinnedPage, setPinnedPage] = useState(null);
  const [hoveredPage, setHoveredPage] = useState(null);
  const highlightedPage = hoveredPage ?? pinnedPage;

  const checkInsTf = useChartTimeFrame(eventId);
  const screenTf = useChartTimeFrame(eventId);

  const checkInsOverTime = useMemo(
    () =>
      sliceByDay(tickets?.checkInsOverTime, checkInsTf.resolved).map((d) => ({
        day: formatDay(d.day),
        "Check-ins": d.count,
      })),
    [tickets, checkInsTf.resolved]
  );

  // Views per page within the selected window, keyed by page name.
  const windowedViewsByPage = useMemo(() => {
    const map = new Map();
    for (const s of screens?.screens ?? []) {
      const total = sliceByDay(s.days, screenTf.resolved, screenDay).reduce(
        (sum, d) => sum + d.viewCount,
        0
      );
      map.set(s.screenName, total);
    }
    return map;
  }, [screens, screenTf.resolved]);

  // Single source of truth for both charts: pages ordered by views within the
  // selected window, dropped if they have no views in the window, capped at
  // PAGES_CHART_LIMIT and coloured by position. The "App page views" lines and
  // "Total views per page" bars both derive from this, so they always show the
  // same pages and colours.
  const pagesSeries = useMemo(
    () =>
      (screens?.perPageDaily?.pages ?? [])
        .filter((name) => (windowedViewsByPage.get(name) ?? 0) > 0)
        .sort(
          (a, b) =>
            (windowedViewsByPage.get(b) ?? 0) -
            (windowedViewsByPage.get(a) ?? 0)
        )
        .slice(0, PAGES_CHART_LIMIT)
        .map((name, i) => ({
          name,
          color: PAGE_PALETTE[i % PAGE_PALETTE.length],
        })),
    [screens, windowedViewsByPage]
  );

  const pageViewsDaily = useMemo(
    () => sliceByDay(screens?.perPageDaily?.days, screenTf.resolved, screenDay),
    [screens, screenTf.resolved]
  );

  // Height grows with the number of overlapping page lines so few pages stay
  // compact (and pack onto one printed page) while a crowded chart gets room.
  const pagesChartHeight = Math.min(
    500,
    Math.max(220, 200 + pagesSeries.length * 30)
  );

  const pagesTotals = useMemo(
    () =>
      pagesSeries
        .map((s) => ({
          page: s.name,
          Views: windowedViewsByPage.get(s.name) ?? 0,
          color: s.color,
        }))
        .sort((a, b) => b.Views - a.Views),
    [pagesSeries, windowedViewsByPage]
  );

  return (
    <Box onClick={() => setPinnedPage(null)}>
      <SectionShell
        id="attendeeAccess"
        title="Attendee access and check-in"
        description="Tickets, check-in and how the app was used."
        isPending={isPending}
        isError={isError}
        error={error}
        eventId={eventId}
        analysisKey="attendeeAccess"
      >
        {(tickets?.totalTickets ?? 0) > 0 && (
          <StatGrid>
            <Stat
              part="totalTickets"
              label="Total tickets"
              value={formatNum(tickets?.totalTickets)}
            />
            {(tickets?.checkedInTickets ?? 0) > 0 && (
              <Stat
                part="checkedIn"
                label="Checked in"
                value={formatNum(tickets?.checkedInTickets)}
              />
            )}
            {Number(tickets?.checkInRate) > 0 && (
              <Stat
                part="checkInRate"
                label="Check-in rate"
                value={formatPct(tickets?.checkInRate)}
              />
            )}
          </StatGrid>
        )}

        {(tickets?.checkedInTickets ?? 0) > 0 && (
          <DailyAreaChart
            part="checkInsOverTime"
            title="Check-ins over time"
            data={checkInsOverTime}
            series={[{ name: "Check-ins", color: "teal.6" }]}
            headerRight={checkInsTf.control}
          />
        )}

        <DailyAreaChart
          part="appPageViews"
          title="App page views during the event"
          data={pageViewsDaily}
          series={pagesSeries}
          height={pagesChartHeight}
          curveType="linear"
          withFill={false}
          bucketSelector
          withLegend={false}
          highlightedSeries={highlightedPage}
          // Up to 10 overlapping lines — per-point labels would be unreadable,
          // so the printed report leaves this chart label-free.
          withPrintPointLabels={false}
          headerRight={screenTf.control}
        />

        <RankedBarChart
          part="viewsPerPage"
          title="Total views per page"
          data={pagesTotals}
          categoryKey="page"
          limit={PAGES_CHART_LIMIT}
          series={[{ name: "Views", color: "blue.6" }]}
          onBarClick={setPinnedPage}
          onBarHover={setHoveredPage}
        />
      </SectionShell>
    </Box>
  );
}

export default AttendeeAccess;
